import React, { useRef, useEffect, useState, useContext } from "react";
import axios from "axios";
import { createPortal } from "react-dom";
import { AuthContext } from "react-oauth2-code-pkce";
import RegisterMember from "../RegisterMember";

/* =========================================================================
   PERSON CARD WITH PORTAL MENU
   ========================================================================= */
const PersonCard = ({ person, onAddMember, onRemove, onEdit, label }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });

  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  const toggleMenu = (e) => {
    e.stopPropagation();

    if (!menuOpen) {
      const rect = triggerRef.current.getBoundingClientRect();
      setMenuPos({
        top: rect.bottom + 4,
        left: rect.right - 90,
      });
    }

    setMenuOpen((prev) => !prev);
  };

  useEffect(() => {
    const handler = (e) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target) &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target)
      ) {
        setMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const DEFAULT_AVATAR = "/images/default-avatar.png";
  const MALE_AVATAR = "/images/male-avatar.png";
  const FEMALE_AVATAR = "/images/female-avatar.png";

  const getAvatar = (person) => {
    if (person?.photo) return person.photo;

    switch ((person?.gender || "").toUpperCase()) {
      case "MALE":
        return MALE_AVATAR;
      case "FEMALE":
        return FEMALE_AVATAR;
      default:
        return DEFAULT_AVATAR;
    }
  };

  const handleAvatarError = (e) => {
    e.currentTarget.onerror = null; 
    e.currentTarget.src = DEFAULT_AVATAR;
  };

  return (
    <div className="flex flex-col items-center text-center relative min-w-[90px] person-card">
      {label && (
        <div className="absolute -top-2 left-1/2 -translate-x-1/2 bg-red-600 text-white text-[8px] px-1 py-0.5 rounded z-20">
          {label}
        </div>
      )}

      <div className="w-16 h-20 rounded overflow-hidden shadow bg-gray-100 relative">
        <img
          src={getAvatar(person)}
          alt={person.name}
          className="w-full h-full object-cover"
          onError={handleAvatarError}
        />

        <button
          ref={triggerRef}
          onClick={toggleMenu}
          className="absolute bottom-1 right-1 bg-white text-gray-700 hover:bg-gray-200 shadow rounded-full w-6 h-6 flex items-center justify-center text-lg z-20"
        >
          ⋯
        </button>
      </div>

      {menuOpen &&
        createPortal(
          <div
            ref={menuRef}
            className="fixed bg-white border shadow-xl rounded-md w-28 z-[999999]"
            style={{ top: `${menuPos.top}px`, left: `${menuPos.left}px` }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => {
                setMenuOpen(false);
                onAddMember(person);
              }}
              className="w-full text-left px-2 py-1.5 text-[10px] text-red-600 hover:bg-gray-100"
            >
              ➕ Add
            </button>

            <button
              onClick={() => {
                setMenuOpen(false);
                onEdit(person);
              }}
              className="w-full text-left px-2 py-1.5 text-[10px] text-red-600 hover:bg-gray-100"
            >
              ✏️ Edit
            </button>

            {person.isTerminal && (
              <button
                onClick={() => {
                  setMenuOpen(false);
                  onRemove(person);
                }}
                className="w-full text-left px-2 py-1.5 text-[10px] text-red-600 hover:bg-gray-100"
              >
                🗑️ Delete
              </button>
            )}
          </div>,
          document.body
        )}

      <div className="text-xs mt-1 w-full">
        <div className="font-semibold">{person.name}</div>
        {person.dob && <div className="text-gray-500 text-[10px]">{person.dob}</div>}
      </div>
    </div>
  );
};

/* =========================================================================
   COUPLE DISPLAY
   ========================================================================= */
const Couple = ({ mainPerson, spouses, onAddMember, onRemove, onEdit }) => {
  const ref = useRef(null);
  const [line, setLine] = useState({ left: 0, width: 0 });

  useEffect(() => {
    if (!ref.current) return;

    const measure = () => {
      if (!ref.current) return;
      const cards = Array.from(ref.current.querySelectorAll(".person-card"));
      if (cards.length < 2) return;

      const rects = cards.map((c) => c.getBoundingClientRect());
      const container = ref.current.getBoundingClientRect();

      const leftCenter = rects[0].left - container.left + rects[0].width / 2;
      const rightCenter =
        rects[rects.length - 1].left - container.left + rects[rects.length - 1].width / 2;

      setLine({ left: leftCenter, width: rightCenter - leftCenter });
    };

    const obs = new ResizeObserver(measure);
    obs.observe(ref.current);
    window.addEventListener("resize", measure);

    measure();

    return () => {
      obs.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [spouses]);

  return (
    <div ref={ref} className="relative flex justify-center items-start gap-6">
      {spouses?.length > 0 && line.width > 0 && (
        <>
          <div
            className="absolute h-px bg-gray-400 z-10"
            style={{ top: "40px", left: `${line.left}px`, width: `${line.width}px` }}
          />

          <div
            className="absolute z-20 bg-white px-1"
            style={{
              top: "40px",
              left: `${line.left + line.width / 2}px`,
              transform: "translate(-50%, -50%)",
            }}
          >
            <span className="text-red-600 font-bold text-lg">⚭</span>
          </div>
        </>
      )}

      <PersonCard
        person={mainPerson}
        onAddMember={onAddMember}
        onRemove={onRemove}
        onEdit={onEdit}
        label={mainPerson.label}
      />

      {(spouses || []).map((sp, i) => (
        <PersonCard
          key={i}
          person={sp}
          onAddMember={onAddMember}
          onRemove={onRemove}
          onEdit={onEdit}
          label={sp.label}
        />
      ))}
    </div>
  );
};

/* =========================================================================
   TREE NODE
   ========================================================================= */
const TreeNode = ({ person, onAddMember, onRemove, onEdit }) => {
  const childrenRef = useRef(null);
  const childRefs = useRef([]);
  const [siblingLine, setSiblingLine] = useState({ left: 0, width: 0 });

  const children = person.children || [];
  const spouses = person.spouses || [];

  const hasChildren = children.length > 0;
  const isTerminal = !hasChildren;
  const mainPersonForRender = { ...person, isTerminal };

  useEffect(() => {
    if (!childrenRef.current || !hasChildren) return;

    const measure = () => {
      if (!childrenRef.current) return;

      const refs = childRefs.current.filter(Boolean);
      if (refs.length < 2) return;

      const container = childrenRef.current.getBoundingClientRect();
      const rects = refs.map((r) => r.getBoundingClientRect());

      const firstCenter = rects[0].left - container.left + rects[0].width / 2;
      const lastCenter =
        rects[rects.length - 1].left -
        container.left +
        rects[rects.length - 1].width / 2;

      setSiblingLine({ left: firstCenter, width: lastCenter - firstCenter });
    };

    const obs = new ResizeObserver(measure);
    obs.observe(childrenRef.current);
    window.addEventListener("resize", measure);

    measure();

    return () => {
      obs.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [hasChildren, children]);

  return (
    <div className="flex flex-col items-center relative">
      <div className="relative">
        <div
          className="w-px bg-gray-400 absolute left-1/2 -translate-x-1/2"
          style={{ top: "-16px", height: "16px" }}
        />

        <Couple
          mainPerson={mainPersonForRender}
          spouses={spouses.map((sp) => ({
            ...sp,
            isTerminal: !(sp.children?.length > 0),
          }))}
          onAddMember={onAddMember}
          onRemove={onRemove}
          onEdit={onEdit}
        />

        {hasChildren && (
          <div
            className="absolute w-px bg-gray-400 left-1/2 -translate-x-1/2"
            style={{ top: "100%", height: "18px" }}
          />
        )}
      </div>

      {hasChildren && (
        <div className="relative mt-4">
          {children.length > 1 && siblingLine.width > 0 && (
            <div
              className="absolute h-px bg-gray-400"
              style={{
                top: 0,
                left: `${siblingLine.left}px`,
                width: `${siblingLine.width}px`,
              }}
            />
          )}

          <div ref={childrenRef} className="flex justify-center gap-10 relative">
            {children.map((child, i) => (
              <div
                key={i}
                ref={(el) => (childRefs.current[i] = el)}
                className="relative pt-4"
              >
                <div
                  className="absolute w-px bg-gray-400 left-1/2 -translate-x-1/2"
                  style={{ top: 0, height: "16px" }}
                />

                <TreeNode
                  person={child}
                  onAddMember={onAddMember}
                  onRemove={onRemove}
                  onEdit={onEdit}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/* =========================================================================
   FAMILY TREE WRAPPER
   ========================================================================= */
const FamilyTree = ({ data, id, onUpdateMembers }) => {
  const [zoom, setZoom] = useState(100);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [mode, setMode] = useState("add");
  const [confirmDelete, setConfirmDelete] = useState(null);

  // REFS FOR CENTERING
  const scrollContainerRef = useRef(null);
  const rootNodeRef = useRef(null);

  const { token } = useContext(AuthContext);
  const API_BASE = process.env.REACT_APP_API_BASE_URL;

  // EFFECT TO CENTER ROOT NODE
  useEffect(() => {
    if (rootNodeRef.current) {
      rootNodeRef.current.scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest",
      });
    }
  }, [data]);

  const handleAddMember = (person) => {
    setSelectedPerson(person);
    setMode("add");
    setShowModal(true);
  };

  const handleEditMember = (person) => {
    setSelectedPerson(person);
    setMode("edit");
    setShowModal(true);
  };

  const handleRemoveMember = async (person, internal = false) => {
    if (!internal) {
      setConfirmDelete(person);
      return;
    }

    if (!person?.id) return;

    try {
      await axios.delete(`${API_BASE}/family/${id}/member/${person.id}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      onUpdateMembers && onUpdateMembers();
    } catch (err) {
      console.error("Error removing member:", err);
    }
  };

  const closeModal = () => {
    setSelectedPerson(null);
    setShowModal(false);
    onUpdateMembers && onUpdateMembers();
  };

  return (
    <div 
      ref={scrollContainerRef}
      className="bg-white min-h-screen overflow-auto relative flex flex-col"
    >
      <div className="fixed top-4 right-4 flex gap-2 z-50">
        <button onClick={() => setZoom((z) => Math.max(30, z - 10))} className="bg-gray-200 px-3 py-1 rounded">-</button>
        <button onClick={() => setZoom(100)} className="bg-gray-200 px-3 py-1 rounded">{zoom}%</button>
        <button onClick={() => setZoom((z) => Math.min(150, z + 10))} className="bg-gray-200 px-3 py-1 rounded">+</button>
      </div>

      {data?.name && (
        <div className="p-10 min-w-max flex justify-center">
          <div
            ref={rootNodeRef}
            style={{
              transform: `scale(${zoom / 100})`,
              transformOrigin: "top center",
              transition: "0.2s",
              width: "fit-content"
            }}
          >
            <TreeNode
              person={data}
              onAddMember={handleAddMember}
              onRemove={handleRemoveMember}
              onEdit={handleEditMember}
            />
          </div>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white rounded shadow-lg w-196">
            <RegisterMember
              selectedPerson={selectedPerson}
              editMode={mode === "edit"}
              closeModal={closeModal}
              id={id}
              onUpdateMembers={onUpdateMembers}
            />
          </div>
        </div>
      )}

      {confirmDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-[999999]">
          <div className="bg-white rounded-lg shadow-xl w-80 p-5 text-center">
              <div className="flex justify-center mb-3">
              <img
                src={
                  confirmDelete.photo ||
                  (confirmDelete.gender?.toUpperCase() === "MALE"
                    ? "/images/male-avatar.png"
                    : confirmDelete.gender?.toUpperCase() === "FEMALE"
                    ? "/images/female-avatar.png"
                    : "/images/default-avatar.png")
                }
                alt={confirmDelete.name}
                className="w-20 h-24 object-cover rounded shadow"
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = "/images/default-avatar.png";
                }}
              />
            </div>
            <h2 className="text-lg font-semibold">{confirmDelete.name}</h2>

            {confirmDelete.dob && (
              <p className="text-gray-500 text-sm">{confirmDelete.dob}</p>
            )}

            <p className="mt-3 text-sm">Are you sure you want to delete this member?</p>

            <div className="mt-5 flex justify-center gap-3">
              <button
                className="px-4 py-1.5 rounded bg-gray-300 hover:bg-gray-400"
                onClick={() => setConfirmDelete(null)}
              >
                Cancel
              </button>

              <button
                className="px-4 py-1.5 rounded bg-red-600 hover:bg-red-700 text-white"
                onClick={async () => {
                  await handleRemoveMember(confirmDelete, true);
                  setConfirmDelete(null);
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FamilyTree;